import { Loading } from '@bumble/app/components/Loading';
import NoData from '@bumble/app/components/NoData';
import TimeRange from '@bumble/app/components/TimeRange';
import { TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { COLORS } from '@bumble/app/theme';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime, DurationLikeObject } from 'luxon';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  LineChart,
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  Tooltip,
  Legend,
} from 'recharts';
import { getTooltipLabel } from '@bumble/app/utils/get-date';

function convertData(originalData: any, id: string) {
  const newData = [];

  for (const item of originalData) {
    const key = Object.keys(item)[0];
    const metric = key.split('.').pop() || '';
    let formattedLabel = '';
    if (metric === 'cpu') formattedLabel = 'CPU utilization (%)';
    if (metric === 'memory') formattedLabel = 'Memory usage (%)';
    if (metric === 'diag') formattedLabel = 'Thermal metrics (°C)';
    if (metric === 'rpm') formattedLabel = 'CPU Fan Speed (RPM)';
    if (metric === 'tx') formattedLabel = 'Network out (bytes/sec)';
    if (metric === 'rx') formattedLabel = 'Network in (bytes/sec)';
    const dataPoints = item[key].map((entry: any) => {
      let val: any[] = [];
      if (metric === 'cpu') val[0] = entry['cpu_p'].toFixed(3);
      if (metric === 'memory') val[0] = entry['mem.usage'].toFixed(3);
      if (metric === 'rpm') val[0] = entry['cpu.fan.rpm'];
      if (metric === 'tx') val[0] = entry['tx.bytes'].toFixed(3);
      if (metric === 'rx') val[0] = entry['rx.bytes'].toFixed(3);

      if (metric === 'diag') {
        val[0] = entry['ambient']?.toFixed(3) || 0;
        val[1] = entry['cpu']?.toFixed(3) || 0;
        val[2] = entry['disk']?.toFixed(3) || 0;
      }

      return {
        time: Date.parse(entry['@timestamp']),
        value: val,
      };
    });

    newData.push({
      id: metric,
      label: formattedLabel,
      data: dataPoints,
      percentage: metric === 'cpu' || metric === 'memory'
    });
  }

  return newData;
}

const containerStyle = {
  border: `4px solid ${COLORS.BACKGROUND}`,
  borderRadius: '8px'
};

interface NodeMetricsProps {
  id: string;
}

const NodeMetrics = ({ id }: NodeMetricsProps) => {
  const api = useApi();

  const [timeRange, setTimeRange] = useState<DurationLikeObject>(TIME_BUTTONS[0].value);

  const { isLoading, data, refetch, isRefetching } = useQuery(
    ['nodeMetrics'],
    api.getNodeMetricsApiV1GetNodeMetricsGet.bind(null, {
      id,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: true, cacheTime: 0 },
  );

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const handleRefreshClick = () => {
    refetch();
    toast.success('Refreshed chart data.');
  };

  useEffect(() => {
    refetch();
  }, [timeRange, refetch]);

  if (isLoading || isRefetching) {
    return <Loading />;
  }

  if (id === undefined || !data || data?.data.length === 0 || !Array.isArray(data?.data)) {
    return <NoData />;
  }

  let originalData;

  if (data.data) {
    originalData = data.data;
  }

  const series = convertData(originalData, id);

  function CustomizedTicks(props: any) {
    const { x, y, payload } = props;
    const convertedDate = typeof payload.value === 'string'
      ? new Date(payload.value).valueOf()
      : payload.value;
    const date = DateTime.fromMillis(convertedDate);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date?.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date?.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <TimeRange
        timeValues={TIME_BUTTONS}
        selectedTime={timeRange}
        onTimeChange={handleTimeChange}
        onRefresh={handleRefreshClick}
      />
      <Container>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          {series.map(({ label: seriesLabel, data: seriesData, id }) => (
            <Grid item xs={12} md={6} key={seriesLabel}>
              <Box sx={containerStyle}>
                <Typography variant="h5" sx={{ my: 2, textAlign: 'center' }}>
                  {seriesLabel}
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart
                    margin={{ top: 5, right: 10, left: 20, bottom: 5 }}
                    data={seriesData}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="time"
                      type="category"
                      allowDuplicatedCategory={false}
                      height={60}
                      interval={6}
                      tick={<CustomizedTicks />}
                    />
                    <YAxis
                      orientation="left"
                      type="number"
                      domain={id === 'rpm' ? [0, 5000] : [0, 100]}
                      width={30}
                      tickFormatter={(value: number) => `${new Intl.NumberFormat('en-US', {
                        notation: 'compact',
                        compactDisplay: 'short',
                      }).format(value)}`}
                    />
                    {id === 'cpu' &&
                      <Line strokeWidth={2} dot={false} dataKey="value" name="CPU utilization" />
                    }
                    {id === 'memory' &&
                      <Line strokeWidth={2} dot={false} dataKey="value" name="Memory Usage" />
                    }
                    {id === 'diag' && (
                      <>
                        <Line
                          strokeWidth={2}
                          dot={false}
                          dataKey="value[0]"
                          name="Ambient Temperature"
                          stroke="#ECC052"
                        />
                        <Line
                          strokeWidth={2}
                          dot={false}
                          dataKey="value[1]"
                          name="CPU Temperature"
                          stroke="#5F467E"
                        />
                        <Line
                          strokeWidth={2}
                          dot={false}
                          dataKey="value[2]"
                          name="Disk Temperature"
                          stroke="#6F9C6F"
                        />
                        <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
                      </>
                    )}
                    {id === 'rpm' &&
                      <Line
                        strokeWidth={2}
                        dot={false}
                        dataKey="value"
                        name="CPU Fan Speed"
                      />
                    }
                    <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default NodeMetrics;
